<script setup lang="ts">
import ContainerSelectionForm from '@/components/rc/shipments/components/ContainerSelectionForm.vue'
import DamagesInputForm, { type IDamagesInputFormExposed } from '@/components/rc/shipments/forms/DamagesInputForm.vue'
import { useContainers } from '@/composables/rc/containers'
import { ContainerTypeSelectOptions, type INewContainer } from '@/types/rc/containers'
import type { IContainersSetup, IDamageInstance } from '@/types/rc/shipments'
import { Icon } from '@iconify/vue'
import type { ElScrollbar, FormInstance, FormItemRule } from 'element-plus'
import { last } from 'lodash'
import { computed, reactive, ref } from 'vue'
import { vMaska } from 'maska/vue'

const open = defineModel<boolean>()

const damagesRef = ref<IDamagesInputFormExposed>()

const innerRef = ref<HTMLDivElement>()
const scrollbarRef = ref<InstanceType<typeof ElScrollbar>>()

const props = defineProps<{
  workOrder?: string
  customer?: string
  shipment: any
}>()

const collapseTabs = ref(['containersAccordion', 'damagesAccordion'])

const damagesList = ref<IDamageInstance[]>([])

const showAddContainerSelectionForm = ref(false)

// Form config

const newContainer = (id: number, type?: string): INewContainer =>
  structuredClone({
    id,
    type,
    number: '',
    expected_miners: null,
    damages: [],
    miners: [],
  })

const formRef = ref<FormInstance>()
const containersListDynamicForm = reactive<{
  containers: INewContainer[]
}>({
  containers: [newContainer(0)],
})

const containerTypeValidationRules = reactive<FormItemRule[]>([
  {
    required: true,
    message: 'Container type is required.',
    trigger: ['blur', 'change'],
  },
])

const containerNumberValidationRules = reactive<FormItemRule[]>([
  {
    required: true,
    message: 'Container number is required.',
    trigger: ['blur', 'change'],
  },
  {
    type: 'pattern',
    pattern: /^[a-zA-Z0-9\-_()]+$/,
    message: 'Container number is not valid',
    trigger: ['blur', 'change'],
  },
])

const expectedMinersFieldValidationRules = reactive([
  {
    required: true,
    min: 1,
    trigger: ['blur', 'change'],
    validator: (rule: any, value: any) => {
      if (!value) {
        throw new Error('Number of miners is required.')
      }
      if (value && value < 1) {
        throw new Error('Number of miners must be greater than 0.')
      }
      return true
    },
  },
])

const disableSubmitButton = computed(() => {
  return !containersListDynamicForm.containers.length
})

// Server call

const { bulkAddContainersMutation } = useContainers()
const { mutate: addBulkContainers, isPending, isError, error } = bulkAddContainersMutation

const submitBulkAddContainers = async () => {
  if (!formRef.value || !damagesRef.value) return

  expandAccordions()

  const containersFormValid = await formRef.value?.validate()
  const damagesFormValid = await damagesRef.value?.validate()

  if (containersFormValid && damagesFormValid) {
    addBulkContainers(
      {
        shipment_id: props.shipment.id,
        shipment_kind: props.shipment.type,
        containers: containersListDynamicForm.containers.map((c) => ({ ...c, kind: props.shipment.type })),
        damages: damagesList.value,
      },
      {
        onSuccess: () => {
          open.value = false
        },
        onError: handleScrollTop,
      }
    )
  }
}

// Scroll events
const handleScrollTop = () => {
  scrollbarRef.value!.setScrollTop(0)
}

const handleScrollBottom = () => {
  const max = innerRef.value!.clientHeight + 250

  setTimeout(() => {
    scrollbarRef.value!.setScrollTop(max)
  }, 0)
}

// Container placeholders creation
const handleContainerSelectionFormSubmit = (containerSetup: IContainersSetup) => {
  showAddContainerSelectionForm.value = false

  if (containerSetup.amount) {
    for (let i = 0; i < containerSetup.amount; i++) {
      containersListDynamicForm.containers.push(
        newContainer(containersListDynamicForm.containers.length + 1, containerSetup.type)
      )
    }
  }
}

const handleAddSingleContainer = () => {
  const lastContainerType = last(containersListDynamicForm.containers)
  containersListDynamicForm.containers.push(
    newContainer(containersListDynamicForm.containers.length + 1, lastContainerType?.type)
  )
}

const handleContainerSelectionFormCancel = () => {
  showAddContainerSelectionForm.value = false
}

const removeContainer = (id: number) => {
  // Find it so we have the container number to delete its damages
  const container = containersListDynamicForm.containers.find((container) => container.id === id)
  if (!container) return

  // Actually remove it
  containersListDynamicForm.containers = containersListDynamicForm.containers.filter((container) => container.id !== id)

  // Remove all damages for this container
  damagesList.value = damagesList.value.filter((damage) => damage.containerNumber !== container.number)
}

const resetForm = () => {
  formRef.value?.resetFields()

  showAddContainerSelectionForm.value = false

  containersListDynamicForm.containers.splice(0)
  containersListDynamicForm.containers = [newContainer(0)]

  if (damagesRef.value) damagesRef.value.reset()
}

const expandAccordions = () => {
  collapseTabs.value = ['containersAccordion', 'damagesAccordion']
}
</script>

<template>
  <el-dialog
    v-model="open"
    width="600"
    data-testid="container-management-dialog"
    @open="expandAccordions"
    @closed="resetForm"
  >
    <template #header>
      <h3>Add Containers</h3>
    </template>

    <el-scrollbar ref="scrollbarRef" max-height="500px">
      <div ref="innerRef" class="flex flex-col gap-4 py-2 overflow-y-hidden">
        <!-- Header data items -->
        <div class="grid grid-cols-2 w-max gap-2">
          <!-- WO -->
          <p class="color-text-tertiary">Work Order</p>
          <p v-if="props.workOrder" class="font-400">{{ props.workOrder }}</p>
          <!-- Customer -->
          <p class="color-text-tertiary">Customer</p>
          <p v-if="props.customer" class="font-400">{{ props.customer }}</p>
        </div>

        <hr class="border-fill-tertiary" />

        <!-- Error display -->
        <div v-if="isError" class="color-text-error pb-spacing-xs">
          <div>Error: {{ error }}</div>
        </div>

        <!-- Accordion -->
        <div class="flex flex-col px-4">
          <el-collapse v-model="collapseTabs" class="border-none flex flex-col gap-4">
            <!-- Containers section -->
            <el-collapse-item class="flex flex-col items-stretch gap-2" name="containersAccordion">
              <template #title>Containers</template>

              <el-form
                v-if="containersListDynamicForm.containers.length"
                ref="formRef"
                :disabled="isPending"
                :model="containersListDynamicForm"
                :scroll-into-view-options="{ behavior: 'smooth', block: 'start' }"
                scroll-to-error
              >
                <!-- One config row per container -->
                <div
                  v-for="(container, index) in containersListDynamicForm.containers"
                  :key="container.id"
                  class="group flex flex-col gap-2"
                >
                  <!-- Row header -->
                  <div class="flex justify-between items-center">
                    <p class="py-1">Container #{{ index + 1 }}</p>
                    <Icon
                      class="cursor-pointer color-text-component-label hidden group-hover:block"
                      icon="material-symbols:delete-outline"
                      height="20"
                      :inline="true"
                      @click="removeContainer(container.id)"
                    />
                  </div>

                  <!-- Row inputs -->
                  <div class="flex flex-col gap-sm">
                    <!-- Container type -->
                    <div class="flex flex-col gap-1">
                      <div class="flex flex-row gap-xs">
                        <el-form-item
                          :key="container.id"
                          :prop="`containers.${index}.type`"
                          :rules="containerTypeValidationRules"
                        >
                          <el-select v-model="container.type" placeholder="Type" filterable default-first-option>
                            <el-option
                              v-for="option in ContainerTypeSelectOptions"
                              :key="option.key"
                              :label="option.value"
                              :value="option.value"
                            />
                          </el-select>
                        </el-form-item>

                        <!-- Container number -->
                        <el-form-item
                          :key="container.id"
                          :prop="`containers.${index}.number`"
                          :rules="containerNumberValidationRules"
                        >
                          <el-input
                            v-model="container.number"
                            placeholder="Number"
                            title="Number"
                            @change="damagesRef?.resetDamagesSelection()"
                          />
                        </el-form-item>

                        <!-- Expected miners -->
                        <el-form-item
                          :key="container.id"
                          :prop="`containers.${index}.expected_miners`"
                          :rules="expectedMinersFieldValidationRules"
                        >
                          <el-input
                            v-model="container.expected_miners"
                            title="Expected Miners"
                            placeholder="Expected Miners"
                            type="number"
                            min="1"
                          />
                        </el-form-item>
                      </div>

                      <div class="flex flex-row gap-xs">
                        <el-form-item :key="container.id" :prop="`containers.${index}.width`">
                          <el-input
                            v-model="container.length"
                            v-maska
                            data-maska="9999.9"
                            data-maska-tokens="9:\d:optional"
                            placeholder="Length (in)"
                          />
                        </el-form-item>
                        <el-form-item :key="container.id" :prop="`containers.${index}.width`">
                          <el-input
                            v-model="container.width"
                            v-maska
                            data-maska="9999.9"
                            data-maska-tokens="9:\d:optional"
                            placeholder="Width (in)"
                          />
                        </el-form-item>
                        <el-form-item :key="container.id" :prop="`containers.${index}.width`">
                          <el-input
                            v-model="container.height"
                            v-maska
                            data-maska="9999.9"
                            data-maska-tokens="9:\d:optional"
                            placeholder="Height (in)"
                          />
                        </el-form-item>
                        <el-form-item :key="container.id" :prop="`containers.${index}.weight`">
                          <el-input
                            v-model="container.weight"
                            v-maska
                            data-maska="9999.9"
                            data-maska-tokens="9:\d:optional"
                            placeholder="Weight (lb)"
                          />
                        </el-form-item>
                      </div>
                    </div>
                  </div>
                </div>
              </el-form>

              <!-- Add containers button -->
              <div class="w-full flex flex-row justify-between">
                <el-link v-if="!showAddContainerSelectionForm" type="primary" @click="handleAddSingleContainer">
                  <Icon icon="material-symbols:add" height="20" :inline="true" />
                  Add single
                </el-link>
                <el-link
                  v-if="!showAddContainerSelectionForm"
                  type="primary"
                  @click="showAddContainerSelectionForm = true"
                >
                  <Icon icon="material-symbols:add" height="20" :inline="true" />
                  Add many
                </el-link>
              </div>

              <!-- Add containers pseudo-row -->
              <ContainerSelectionForm
                v-if="showAddContainerSelectionForm"
                :select-options="ContainerTypeSelectOptions"
                @container-selection-form-submit="handleContainerSelectionFormSubmit"
                @container-selection-form-cancel="handleContainerSelectionFormCancel"
              />
            </el-collapse-item>

            <hr class="border-fill-tertiary" />

            <!-- Damages section -->
            <el-collapse-item class="flex flex-col items-stretch gap-2" name="damagesAccordion">
              <template #title>Damages</template>
              <DamagesInputForm
                ref="damagesRef"
                v-model="damagesList"
                :disabled="isPending"
                :available-containers="containersListDynamicForm.containers"
                @add-damages="handleScrollBottom"
              />
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>
    </el-scrollbar>
    <template #footer>
      <el-button data-testid="container-management-dialog-cancel-btn" @click="open = false">Cancel</el-button>
      <el-button :loading="isPending" :disabled="disableSubmitButton" type="primary" @click="submitBulkAddContainers">
        Submit
      </el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
:deep() {
  .el-collapse-item {
    &__header {
      font-size: 16px;
      font-weight: 600;
      margin: 0;
      border: none;
    }

    &__wrap {
      border-bottom: none;
    }

    &__content {
      padding-bottom: 0;
    }
  }
}
</style>
